import { FC, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Calendar,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Settings,
  FileText,
  BarChart3,
  Scale,
  UserCircle,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGuard, type Permission } from './PermissionGuard';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: Permission;
}

const navItems: NavItem[] = [
  {
    label: 'Overview',
    path: '/admin',
    icon: <LayoutDashboard className="h-5 w-5" />,
    permission: 'VIEW_DASHBOARD',
  },
  {
    label: 'Therapists',
    path: '/admin/therapists',
    icon: <Users className="h-5 w-5" />,
    permission: 'MANAGE_THERAPISTS',
  },
  {
    label: 'Services',
    path: '/admin/services',
    icon: <Briefcase className="h-5 w-5" />,
    permission: 'MANAGE_SERVICES',
  },
  {
    label: 'Bookings',
    path: '/admin/bookings',
    icon: <Calendar className="h-5 w-5" />,
    permission: 'MANAGE_BOOKINGS',
  },
  {
    label: 'Customers',
    path: '/admin/customers',
    icon: <UserCircle className="h-5 w-5" />,
    permission: 'MANAGE_USERS',
  },
  {
    label: 'Reconciliation',
    path: '/admin/reconciliation',
    icon: <Scale className="h-5 w-5" />,
    permission: 'VIEW_REPORTS',
  },
  {
    label: 'Analytics',
    path: '/admin/analytics',
    icon: <BarChart3 className="h-5 w-5" />,
    permission: 'VIEW_REPORTS',
  },
  {
    label: 'Audit Logs',
    path: '/admin/audit-logs',
    icon: <FileText className="h-5 w-5" />,
    permission: 'VIEW_AUDIT_LOGS',
  },
  {
    label: 'Settings',
    path: '/admin/settings',
    icon: <Settings className="h-5 w-5" />,
    permission: 'MANAGE_SETTINGS',
  },
];

export const AdminSidebar: FC<AdminSidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen,
  onMobileClose,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLElement>(null);

  useFocusTrap(sidebarRef, { enabled: isMobileOpen });
  useEscapeKey(onMobileClose, isMobileOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      ref={sidebarRef}
      id="admin-sidebar"
      role="complementary"
      aria-label="Admin navigation"
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-gray-900 text-white transition-all duration-300',
        'w-64',
        isCollapsed ? 'lg:w-16' : 'lg:w-64',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
        <span className={cn('text-lg font-semibold', isCollapsed && 'lg:hidden')}>
          Admin Panel
        </span>
        <button
          onClick={onToggle}
          className={cn(
            'hidden lg:block rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white',
            isCollapsed && 'mx-auto'
          )}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => (
          <PermissionGuard key={item.path} permission={item.permission}>
            <NavLink
              to={item.path}
              end={item.path === '/admin'}
              onClick={onMobileClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                  isCollapsed && 'lg:justify-center lg:px-2'
                )
              }
              title={isCollapsed ? item.label : undefined}
            >
              {item.icon}
              <span className={cn(isCollapsed && 'lg:hidden')}>{item.label}</span>
            </NavLink>
          </PermissionGuard>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-700 p-2">
        {/* User Info */}
        {user && (
          <div className={cn('mb-2 rounded-lg bg-gray-800 p-3', isCollapsed && 'lg:hidden')}>
            <p className="text-sm font-medium text-white">{user.name}</p>
            <p className="text-xs text-gray-400">{user.email}</p>
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800 hover:text-white',
            isCollapsed && 'lg:justify-center lg:px-2'
          )}
          title={isCollapsed ? 'Logout' : undefined}
        >
          <LogOut className="h-5 w-5" />
          <span className={cn(isCollapsed && 'lg:hidden')}>Logout</span>
        </button>
      </div>
    </aside>
  );
};
